/**
 * Row Calculations - Pure Utility Functions
 *
 * Functions for grouping and querying containers by row.
 * All functions are pure (no side effects) and easily testable.
 *
 * Performance: O(n) where n = number of containers in canvas
 */

import type { ContainerAPI } from '../../types/api.types';
import type { ContainerRow, SlotAvailability, RowStats } from '../types/container-row.types';
import { CONTAINER_CONSTRAINTS } from '../constants/constraints';

// ============================================================================
// Row Grouping Functions
// ============================================================================

/**
 * Groups containers by their rowIndex property
 *
 * @param containers - Array of containers to group
 * @returns Record mapping rowIndex to containers in that row
 *
 * Time: O(n), Space: O(n)
 */
export const groupContainersByRow = (
  containers: ContainerAPI[]
): Record<number, ContainerAPI[]> => {
  return containers.reduce<Record<number, ContainerAPI[]>>((acc, container) => {
    const rowIndex = container.rowIndex ?? 0;
    if (!acc[rowIndex]) {
      acc[rowIndex] = [];
    }
    acc[rowIndex].push(container);
    return acc;
  }, {});
};

/**
 * Converts containers to ContainerRow[] for UI rendering
 *
 * @param containers - Array of containers
 * @param maxPerRow - Maximum containers per row (default: 3)
 * @returns Array of ContainerRow objects sorted by rowIndex
 *
 * Time: O(n), Space: O(n)
 */
export const getContainerRows = (
  containers: ContainerAPI[],
  maxPerRow: number = CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW
): ContainerRow[] => {
  const grouped = groupContainersByRow(containers);

  return Object.entries(grouped)
    .map(([rowIndex, rowContainers]) => {
      // Sort containers within row by order property
      const sortedContainers = [...rowContainers].sort((a, b) => a.order - b.order);

      // Calculate total width
      const totalWidth = sortedContainers.reduce((sum, c) => {
        return sum + parseWidth(c.configuration?.width || '100%');
      }, 0);

      return {
        rowIndex: Number(rowIndex),
        containers: sortedContainers,
        totalWidth,
        canAddMore: sortedContainers.length < maxPerRow,
      };
    })
    .sort((a, b) => a.rowIndex - b.rowIndex);
};

/**
 * Parses a width string to a number
 * Handles percentages and returns the numeric value
 *
 * @param width - Width string (e.g., "33%", "100%", "50")
 * @returns Numeric width value
 */
export const parseWidth = (width: string): number => {
  const numericValue = parseFloat(width.replace('%', ''));
  return isNaN(numericValue) ? 100 : numericValue;
};

// ============================================================================
// Row Query Functions
// ============================================================================

/**
 * Checks if a row can accept more containers
 *
 * @param containers - All containers in the canvas
 * @param rowIndex - Row to check
 * @param maxPerRow - Maximum containers per row
 * @returns Whether the row can accept more containers
 *
 * Time: O(n), Space: O(1)
 */
export const canAddToRow = (
  containers: ContainerAPI[],
  rowIndex: number,
  maxPerRow: number = CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW
): boolean => {
  const count = getRowContainerCount(containers, rowIndex);
  return count < maxPerRow;
};

/**
 * Gets the number of containers in a specific row
 *
 * @param containers - All containers in the canvas
 * @param rowIndex - Row to count
 * @returns Number of containers in the row
 *
 * Time: O(n), Space: O(1)
 */
export const getRowContainerCount = (
  containers: ContainerAPI[],
  rowIndex: number
): number => {
  return containers.filter((c) => (c.rowIndex ?? 0) === rowIndex).length;
};

/**
 * Gets containers in a specific row
 *
 * @param containers - All containers in the canvas
 * @param rowIndex - Row to get
 * @returns Containers in the row, sorted by order
 *
 * Time: O(n), Space: O(k) where k = containers in row
 */
export const getContainersInRow = (
  containers: ContainerAPI[],
  rowIndex: number
): ContainerAPI[] => {
  return containers
    .filter((c) => (c.rowIndex ?? 0) === rowIndex)
    .sort((a, b) => a.order - b.order);
};

/**
 * Finds the next available slot for a new container
 *
 * @param containers - All containers in the canvas
 * @param maxPerRow - Maximum containers per row
 * @returns Slot availability information
 *
 * Time: O(n), Space: O(n)
 */
export const getNextAvailableSlot = (
  containers: ContainerAPI[],
  maxPerRow: number = CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW
): SlotAvailability => {
  if (containers.length === 0) {
    return {
      available: true,
      rowIndex: 0,
      positionInRow: 0,
      createsNewRow: true,
    };
  }

  const rows = getContainerRows(containers, maxPerRow);

  // Find first row with space
  for (const row of rows) {
    if (row.canAddMore) {
      return {
        available: true,
        rowIndex: row.rowIndex,
        positionInRow: row.containers.length,
        createsNewRow: false,
      };
    }
  }

  // All rows full - need new row
  const maxRowIndex = Math.max(...rows.map((r) => r.rowIndex));
  return {
    available: true,
    rowIndex: maxRowIndex + 1,
    positionInRow: 0,
    createsNewRow: true,
  };
};

/**
 * Gets the maximum row index in the containers
 *
 * @param containers - All containers in the canvas
 * @returns Maximum row index, or -1 if no containers
 *
 * Time: O(n), Space: O(1)
 */
export const getMaxRowIndex = (containers: ContainerAPI[]): number => {
  if (containers.length === 0) return -1;
  return Math.max(...containers.map((c) => c.rowIndex ?? 0));
};

/**
 * Gets statistics about rows in the canvas
 *
 * @param containers - All containers in the canvas
 * @param maxPerRow - Maximum containers per row
 * @returns Row statistics
 *
 * Time: O(n), Space: O(n)
 */
export const getRowStats = (
  containers: ContainerAPI[],
  maxPerRow: number = CONTAINER_CONSTRAINTS.MAX_CONTAINERS_PER_ROW
): RowStats => {
  const rows = getContainerRows(containers, maxPerRow);

  return {
    totalRows: rows.length,
    totalContainers: containers.length,
    fullRows: rows.filter((r) => r.containers.length === maxPerRow).length,
    partialRows: rows.filter((r) => r.containers.length > 0 && r.containers.length < maxPerRow).length,
    emptyRows: rows.filter((r) => r.containers.length === 0).length,
  };
};

// ============================================================================
// Row Index Management
// ============================================================================

/**
 * Recalculates row indices to ensure they are sequential (0, 1, 2, ...)
 * Call this after removing containers to close gaps
 *
 * @param containers - All containers in the canvas
 * @returns New containers array with normalized row indices
 *
 * Time: O(n log n), Space: O(n)
 */
export const normalizeRowIndices = (
  containers: ContainerAPI[]
): ContainerAPI[] => {
  const grouped = groupContainersByRow(containers);
  const sortedRowIndices = Object.keys(grouped).map(Number).sort((a, b) => a - b);

  // Create mapping from old row index to new sequential index
  const rowIndexMap = new Map<number, number>();
  sortedRowIndices.forEach((oldIndex, newIndex) => {
    rowIndexMap.set(oldIndex, newIndex);
  });

  // Apply new row indices
  return containers.map((container) => {
    const oldRowIndex = container.rowIndex ?? 0;
    const newRowIndex = rowIndexMap.get(oldRowIndex) ?? 0;

    if (oldRowIndex === newRowIndex) {
      return container; // No change needed
    }

    return {
      ...container,
      rowIndex: newRowIndex,
      metadata: container.metadata
        ? { ...container.metadata, rowIndex: newRowIndex }
        : undefined,
    };
  });
};

/**
 * Shifts row indices after a certain index
 * Used when inserting a new row to make space
 *
 * @param containers - All containers in the canvas
 * @param afterRowIndex - Row index after which to shift
 * @param shiftAmount - Amount to shift (positive = down, negative = up)
 * @returns New containers array with shifted row indices
 *
 * Time: O(n), Space: O(n)
 */
export const shiftRowIndices = (
  containers: ContainerAPI[],
  afterRowIndex: number,
  shiftAmount: number
): ContainerAPI[] => {
  return containers.map((container) => {
    const rowIndex = container.rowIndex ?? 0;

    if (rowIndex > afterRowIndex) {
      const newRowIndex = rowIndex + shiftAmount;
      return {
        ...container,
        rowIndex: newRowIndex,
        metadata: container.metadata
          ? { ...container.metadata, rowIndex: newRowIndex }
          : undefined,
      };
    }

    return container;
  });
};

// ============================================================================
// Order Management Within Row
// ============================================================================

/**
 * Recalculates order values within a row to be sequential (0, 1, 2)
 *
 * @param containers - Containers in a single row
 * @returns Containers with normalized order values
 *
 * Time: O(n log n), Space: O(n)
 */
export const normalizeOrderInRow = (
  containers: ContainerAPI[]
): ContainerAPI[] => {
  const sorted = [...containers].sort((a, b) => a.order - b.order);

  return sorted.map((container, index) => {
    if (container.order === index) {
      return container; // No change needed
    }
    return { ...container, order: index };
  });
};

/**
 * Gets the next order value for a new container in a row
 *
 * @param containers - All containers in the canvas
 * @param rowIndex - Row to get next order for
 * @returns Next order value for the row
 *
 * Time: O(n), Space: O(1)
 */
export const getNextOrderInRow = (
  containers: ContainerAPI[],
  rowIndex: number
): number => {
  const rowContainers = getContainersInRow(containers, rowIndex);
  if (rowContainers.length === 0) return 0;
  return Math.max(...rowContainers.map((c) => c.order)) + 1;
};
